import { Empty, Form, message, Row } from "antd";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetOrganization } from "../../../../../api/v1";
import { AppDispatch } from "../../../../../redux/store";
import { TemplateField } from "../../../../../types/template";
import { SectionLoader } from "../../../../atoms/SectionLoader/SectionLoader";
import { ButtonGroup, ButtonGroupProps } from "../../../../molecules/Button/ButtonGroup";
import { DynamicFields, transformDynamicFields } from "../../../../organism/DynamicFields";
import { getBookingDetail } from "../../ducks/actions";
import { useUpdateCustomFields } from "./api";

type CustomFieldsFormProps = {
  onClose: () => void;
};

const transformCustomFieldDefaultValue = (field: TemplateField, value: unknown) => {
  if (field.type !== "select" || !value || typeof value !== "object") {
    return value;
  }

  const selectValue = value as Record<string, unknown>;
  if ("key" in selectValue && "value" in selectValue) {
    return {
      value: selectValue.key,
      label: String(selectValue.value ?? ""),
    };
  }

  return value;
};

export const CustomFieldsForm = (props: CustomFieldsFormProps) => {
  const { onClose } = props;

  const dispatch = useDispatch<AppDispatch>();
  const { bookingDetails } = useSelector((state: any) => state.newBooking);

  const { organization, isLoadingOrganization } = useGetOrganization({
    params: { filterkeys: "customFields.lease" },
  });

  const { updateCustomFieldsRequest, isPendingUpdateCustomFields } = useUpdateCustomFields({
    onSuccess: async () => {
      onClose();
      if (bookingDetails?.leaseId) await dispatch(getBookingDetail(bookingDetails?.leaseId));
    },
    onError: (error) => {
      message.error(error?.response?.data?.error ?? "Failed to update custom fields");
    },
  });

  const dynamicFieldsTemplate = useMemo(() => {
    const template = (organization?.customFields?.lease as TemplateField[]) ?? [];
    const bookingCustomFields = bookingDetails?.customFields as Record<string, unknown> | undefined;
    const hasBookingCustomFields = bookingCustomFields && Object.keys(bookingCustomFields).length > 0;

    if (!hasBookingCustomFields) return template;

    return template.map((field) => ({
      ...field,
      default: transformCustomFieldDefaultValue(field, bookingCustomFields[field.name]),
    }));
  }, [bookingDetails?.customFields, organization?.customFields]);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm();

  const defaultProps = { control, setValue };

  const onSubmit = async (data) => {
    if (!bookingDetails?.leaseId) {
      message.error("Unable to update custom fields because lease details are missing");
      return;
    }

    const customFields = transformDynamicFields(data, { selectOptionAsObject: true });

    await updateCustomFieldsRequest({
      leaseId: bookingDetails?.leaseId,
      payload: { customFields },
    });
  };

  const submitPrimaryButton: ButtonGroupProps["primaryButton"] = {
    label: "Submit",
    htmlType: "submit",
    loading: isPendingUpdateCustomFields,
  };

  const submitSecondaryButton: ButtonGroupProps["secondaryButton"] = {
    label: "Cancel",
    onClick: onClose,
    disabled: isPendingUpdateCustomFields,
  };

  return (
    <SectionLoader isLoading={isLoadingOrganization} containerStyle={{ minHeight: "14rem" }}>
      {dynamicFieldsTemplate.length === 0 ? (
        <Empty description="No custom fields available" style={{ margin: "6rem" }} />
      ) : (
        <Form layout="vertical" className="bf-main" onFinish={handleSubmit(onSubmit)}>
          <Row gutter={16}>
            <DynamicFields template={dynamicFieldsTemplate} {...defaultProps} errors={errors} colSpan={12} />
            <ButtonGroup
              primaryButton={submitPrimaryButton}
              secondaryButton={submitSecondaryButton}
              style={{ marginTop: "1rem", paddingInline: "0.5rem" }}
            />
          </Row>
        </Form>
      )}
    </SectionLoader>
  );
};
